import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, KeyRound } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { useAuth } from '../../context/AuthContext';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import { post } from '../../api/client';
import { Card, Button, Avatar, Field, Input, Loader, SectionTitle } from '../../components/UI';
import { fmtDate } from '../../utils/format';

export default function AttendeeProfile() {
  const { logout } = useAuth();
  const { events, eventId, setEventId } = useEvents();
  const toast = useToast();
  const nav = useNavigate();
  const { data: me, loading } = useFetch('/auth/me');
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '' });
  const [saving, setSaving] = useState(false);

  if (loading || !me) return <Loader full label="Loading your profile" />;

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.newPassword.length < 6) return toast.error('New password must be at least 6 characters');
    setSaving(true);
    try {
      const res = await post('/auth/change-password', pw);
      toast.success(res.message);
      setPw({ currentPassword: '', newPassword: '' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const rows = [['Email', me.email], ['Mobile', me.mobile], ['Employee code', me.employeeCode], ['Department', me.department]].filter(([, v]) => v);

  return (
    <>
      <SectionTitle title="Profile" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, alignItems: 'start' }}>
        <Card padding={24}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <Avatar name={me.name} size={58} vip={me.isVip} />
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 18, fontWeight: 700 }}>{me.name}</p>
              <p style={{ fontSize: 13, color: 'var(--text-mid)' }}>{me.designation || 'Attendee'}</p>
            </div>
          </div>
          <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 9 }}>
            {rows.map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                <span style={{ color: 'var(--text-lo)' }}>{k}</span>
                <span style={{ textAlign: 'right', wordBreak: 'break-all' }}>{v}</span>
              </div>
            ))}
          </div>
          <Button variant="subtle" icon={LogOut} onClick={() => { logout(); nav('/login'); }}
            style={{ marginTop: 20, width: '100%', color: 'var(--danger)' }}>
            Sign out
          </Button>
        </Card>

        <Card padding={22}>
          <p style={{ fontSize: 15, fontWeight: 600 }}>Your events</p>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {events.map((e) => (
              <button key={e._id} type="button" onClick={() => setEventId(e._id)}
                style={{
                  textAlign: 'left', padding: '11px 13px', borderRadius: 'var(--r)',
                  border: `1px solid ${e._id === eventId ? 'var(--gold-500)' : 'var(--border)'}`,
                  background: e._id === eventId ? 'rgba(239,212,138,0.07)' : 'transparent',
                }}>
                <p style={{ fontSize: 13.5, fontWeight: 500 }}>{e.title}</p>
                <p style={{ fontSize: 12, color: 'var(--text-lo)', marginTop: 2 }}>{fmtDate(e.startDate)} – {fmtDate(e.endDate)}</p>
              </button>
            ))}
          </div>
        </Card>

        <Card padding={22}>
          <p style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}><KeyRound size={15} color="var(--gold-500)" />Password</p>
          <p style={{ fontSize: 12.5, color: 'var(--text-lo)', marginTop: 4, lineHeight: 1.6 }}>
            Only needed if you sign in with email. Mobile sign-in uses a WhatsApp code instead.
          </p>
          <form onSubmit={changePassword} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
            <Field label="Current password">
              <Input type="password" autoComplete="current-password" value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} />
            </Field>
            <Field label="New password">
              <Input type="password" autoComplete="new-password" value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} />
            </Field>
            <Button type="submit" loading={saving}>Update password</Button>
          </form>
        </Card>
      </div>
    </>
  );
}
